import { useAppSelector } from "integrations/redux/hooks"
import { getSidePanelAssetsPlugins } from "plugins/plugin-registry"
import { selectEntitiesFromIds } from "./slice"
import {
    selectSearchAssetsValue,
    selectFilterAssetsValue,
    selectSortAssetsValue,
} from "./slice.new"
import { compareFunctionsBySortMode } from "./constants"
import { FC, useEffect, useMemo, useState } from "react"
import { getUsage } from "features/map/inspector-panel/useInspectorPanelUsage"
import { sortArrayToDesiredOrder } from "tools/arrays/sort"

export type SidePanelEntityData = {
    plugin: ReturnType<typeof getSidePanelAssetsPlugins>[number]
    entityId: string
}
export type SidePanelCategoryData = {
    SidePanelCategoryHeader: FC<{ entityCount: number }>
    entitiesData: SidePanelEntityData[]
}
export type SidePanelCategoryDataOptionalCategoryHeader = {
    SidePanelCategoryHeader: FC<{ entityCount: number }> | null
    entitiesData: SidePanelEntityData[]
}
export type SidePanelAssetsData = Record<string, SidePanelCategoryData>
export type SidePanelAssetsDataOptionalCategoryHeader = Record<
    string,
    SidePanelCategoryDataOptionalCategoryHeader
>

function isCompleteData(
    data: SidePanelAssetsDataOptionalCategoryHeader
): data is SidePanelAssetsData {
    Object.entries(data).forEach(([categoryId, category]) => {
        if (!category.SidePanelCategoryHeader) {
            throw new Error(
                `The side-panel-asset-plugin with category ID: "${categoryId}" does not have a designated CategoryHeader component`
            )
        }
    })
    return true
}

const useAssetsSidePanelData = () => {
    const sidePanelPlugins = getSidePanelAssetsPlugins()
    const data: SidePanelAssetsDataOptionalCategoryHeader = sidePanelPlugins.reduce(
        (acc, curPlugin) => {
            const entities = curPlugin.useSidePanelEntities()
            if (!acc[curPlugin.categoryId]) {
                acc[curPlugin.categoryId] = { SidePanelCategoryHeader: null, entitiesData: [] }
            }
            if (curPlugin.useThisCategoryHeader) {
                if (acc[curPlugin.categoryId].SidePanelCategoryHeader) {
                    throw new Error(
                        `Two or more side-panel-asset-plugin's registered under the category ID: "${curPlugin.categoryId}", both wanting to use their category header.`
                    )
                }
                acc[curPlugin.categoryId].SidePanelCategoryHeader =
                    curPlugin.SidePanelCategoryHeader
            }
            entities
                .filter((entity) => !entity.archivedTimestamp)
                .forEach((entity) => {
                    acc[curPlugin.categoryId].entitiesData.push({
                        plugin: curPlugin,
                        entityId: entity.id,
                    })
                })
            return acc
        },
        {} as SidePanelAssetsDataOptionalCategoryHeader
    )
    const filteredData = Object.entries(data).reduce((acc, [categoryId, data]) => {
        if (data.entitiesData.length === 0) {
            return acc
        }
        acc[categoryId] = data
        return acc
    }, {} as SidePanelAssetsDataOptionalCategoryHeader)
    if (!isCompleteData(filteredData)) {
        // Error will be thrown from isCompleteData
        return {}
    }
    return filteredData
}

const pluginOrder = [
    "Orthomosaic",
    "Hotspots",
    "Fire Perimeter",
    "Colorfinder",
    "Live Stream",
    "Video",
    "Panorama",
] as const

export const useFilteredAndSortedAssetsSidePanelData = () => {
    const sidePanelData = useAssetsSidePanelData()
    const searchAssetsValue = useAppSelector(selectSearchAssetsValue)
    const filterAssetsValue = useAppSelector(selectFilterAssetsValue)
    const sortAssetsValue = useAppSelector(selectSortAssetsValue)
    const entities = useAppSelector(
        selectEntitiesFromIds(
            Object.values(sidePanelData)
                .map((data) => data.entitiesData.map((entityData) => entityData.entityId))
                .flat()
        )
    )
    const entityMap = useMemo(() => {
        return Object.fromEntries(entities.map((entity) => [entity.id, entity]))
    }, [entities])
    const [sizeMap, setSizeMap] = useState(
        Object.fromEntries(entities.map((entity) => [entity.id, 0]))
    )
    useEffect(() => {
        const abortController = new AbortController()
        async function updateSizes() {
            for (const entity of entities) {
                const usage = await getUsage({ entity, abortController })
                const newSize = usage?.usageAmount || 0
                setSizeMap((prev) => {
                    const prevSize = prev[entity.id]
                    if (prevSize === newSize) return prev
                    return { ...prev, [entity.id]: newSize }
                })
            }
        }
        updateSizes()
        return () => {
            abortController.abort("cancel")
        }
    }, [entities])
    const filteredData = Object.fromEntries(
        Object.entries(sidePanelData).map(([key, data]) => [
            key,
            {
                SidePanelCategoryHeader: data.SidePanelCategoryHeader,
                entitiesData: data.entitiesData.filter((entityData) => {
                    const entity = entityMap[entityData.entityId]
                    const matchesSearch = entity.name
                        .toLowerCase()
                        .includes(searchAssetsValue.toLocaleLowerCase())
                    const matchesFilter =
                        !filterAssetsValue.length ||
                        (entity.createdBy?.userId &&
                            filterAssetsValue.includes(entity.createdBy.userId))
                    const matches = matchesSearch && matchesFilter
                    return matches
                }),
            },
        ])
    )
    const compareFn = compareFunctionsBySortMode[sortAssetsValue]
    const sortArr = (data: SidePanelEntityData[]) => {
        data.sort((a, b) => {
            const entityA = entityMap[a.entityId]
            const entityB = entityMap[b.entityId]
            return compareFn(entityA, entityB, sizeMap)
        })
    }
    Object.values(filteredData).forEach((categoryData) => sortArr(categoryData.entitiesData))
    const categories = Object.entries(filteredData).reduce(
        (acc, [categoryId, categoryData]) => {
            if (!categoryData.entitiesData.length) return acc
            acc.push({
                categoryId,
                categoryData,
            })
            return acc
        },
        [] as { categoryId: string; categoryData: SidePanelCategoryData }[]
    )
    sortArrayToDesiredOrder({
        data: categories,
        getSortingValue: (t) => t.categoryId,
        desiredOrder: pluginOrder,
    })
    return categories
}
